import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Benefits as UIBenefits } from '@perxis-auto/ui-kit/lib/components/Benefits';
import { Icon } from '@perxis-auto/ui-kit/lib/components/Icon';
import { CheckboxChangeEvent } from 'antd/es/checkbox';

import getCompatibleBenefits from '@/features/benefits/helpers/getCompatibleBenefits';
import getFootnoteNumber from '@/features/benefits/helpers/getFootnoteNumber';
import getCarBenefits from '@/features/cars/helpers/getCarBenefits';
import useCarData from '@/features/cars/hooks/useCarData';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import { rootActions, rootSelectors } from '@/features/store';
import { withHiddenLazyComponent } from '@/hoc';
import useBenefitsChoice from '@/hooks/useBenefitsChoice';

import { BenefitsProps } from './Benefits.types';

import styles from './Benefits.module.scss';

/**
 * Компонент для отображения списка выгод, с возможностью выбора совместимых.
 */
function Benefits(props: BenefitsProps) {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const benefitsConfig = useAppSelector(rootSelectors.config.benefitsSelector);

  const { data } = useCarData();

  // Hack пока нет селектора, возвращающего ExtendedCarItem
  const extendedCarData = data;
  const carId = extendedCarData?.id;
  const benefits = getCarBenefits(extendedCarData);
  const maxBenefit = extendedCarData?.data.maxBenefit;
  const price = extendedCarData?.data.price;
  const selectedBenefits = useBenefitsChoice(carId, benefits, maxBenefit);

  /** Сумма выбранных выгод */
  const totalBenefit = selectedBenefits.reduce((amount, b) => amount + b.value, 0);

  /** Сумма с учетом выгод */
  const priceWithBenefit = price ? price - totalBenefit : 0;

  const handleOnSelect = useCallback(
    (e: CheckboxChangeEvent, benefitId: React.Key) => {
      if (!carId || !benefits || benefits.length === 0) {
        return null;
      }

      const benefit = benefits.find(b => b.id === benefitId);

      if (!benefit) {
        return null;
      }

      // Если пункт выгоды был выбран
      if (e.target.checked) {
        // Определяем, появились ли несовместимые выгоды.
        // Получаем список совместимых выгод
        const compatibleBenefits = getCompatibleBenefits(
          benefits,
          benefit,
          selectedBenefits,
        );

        // Отфильтруем из списка предварительно выбранных выгод те, что теперь не совместимы
        const selectedBenefitsWithoutIncompatible = selectedBenefits.filter(
          selectedBenefit =>
            compatibleBenefits.some(
              compatibleBenefit => compatibleBenefit === selectedBenefit,
            ),
        );
        // С этих выгод необходимо снять выбор.
        dispatch(
          rootActions.benefitsChoice.setBenefitsChoice({
            id: carId,
            benefits: [...selectedBenefitsWithoutIncompatible, benefit],
          }),
        );

        return;
      }

      // Если отменен выбор выгоды, просто удаляем ее из списка выбранных выгод
      const updatedSelectedBenefits = selectedBenefits.filter(b => b !== benefit);
      dispatch(
        rootActions.benefitsChoice.setBenefitsChoice({
          id: carId,
          benefits: updatedSelectedBenefits,
        }),
      );
    },
    [benefits, selectedBenefits, carId, dispatch],
  );

  const compatibleBenefits = useMemo(() => {
    if (!benefits) {
      return [];
    }

    if (!selectedBenefits.length) {
      return benefits;
    }

    const lastSelectedBenefit = selectedBenefits[selectedBenefits.length - 1];
    const previouslySelectedBenefits = selectedBenefits.slice(
      0,
      selectedBenefits.length - 1,
    );

    const compatibleBenefits = getCompatibleBenefits(
      benefits,
      lastSelectedBenefit,
      previouslySelectedBenefits,
    );

    return [lastSelectedBenefit, ...compatibleBenefits];
  }, [benefits, selectedBenefits]);

  const isBenefitIncompatible = (benefitId: string) =>
    !compatibleBenefits.some(b => b.id === benefitId);

  const isBenefitSelected = (benefitId: string) => {
    return selectedBenefits.some(b => b.id === benefitId);
  };

  const getFootnoteNumberById = (benefitId: string) => {
    const benefit = benefits?.find(b => b.id === benefitId);

    if (!benefit || !benefits) {
      return undefined;
    }

    return getFootnoteNumber(benefits, benefit) || undefined;
  };

  if (!benefits || !benefits.length) {
    return null;
  }

  /** Рассчитывает значение максимальной выгоды. */
  const getMaxBenefit = () => {
    if (!maxBenefit || !maxBenefit.length || !benefits) {
      return 0;
    }

    const amount = maxBenefit.reduce((amount, maxBenefit) => {
      const benefit = benefits.find(b => b.id === maxBenefit.id);

      if (!benefit) {
        return amount;
      }

      return amount + benefit.value;
    }, 0);

    return amount;
  };

  return (
    <div className={styles.benefits}>
      <UIBenefits
        title={{
          label: benefitsConfig.title,
          icon: <Icon name="service-discount" />,
        }}
        header={[
          {
            id: 0,
            label: benefitsConfig.retailPrice,
            value: t('currency', { value: price }),
          },
        ]}
        items={benefits.map(({ displayName, value, id }) => ({
          id,
          value: benefitsConfig.showAmount ? t('currency', { value }) : null,
          label: displayName,
          selected: isBenefitSelected(id),
          incompatible: isBenefitIncompatible(id),
          footnoteNumber: getFootnoteNumberById(id),
        }))}
        onSelectChange={handleOnSelect}
        subtotal={[
          {
            id: 0,
            label: benefitsConfig.totalBenefit,
            value: t('currency', { value: totalBenefit }),
          },
          {
            id: 1,
            label: benefitsConfig.priceWithBenefit,
            value: t('currency', { value: priceWithBenefit }),
          },
        ]}
        footer={{
          text: `${benefitsConfig.maxBenefit}${t('currency', {
            value: getMaxBenefit(),
          })}`,
        }}
      />
    </div>
  );
}

export default withHiddenLazyComponent(
  Benefits,
  rootSelectors.config.benefitsEnableSelector,
);
